import React from "react";
import { Store, Warehouse, Users, Truck, MapPin, Phone, Star, Quote } from "lucide-react";
import useReveal from "../hooks/useReveal";

const points = [
  { icon: Store, title: "A real showroom you can visit", text: "Test the equipment in person at our Seven Hills, Sydney showroom before you buy." },
  { icon: Warehouse, title: "Huge warehouse, stock on hand", text: "We hold serious stock locally — no drop-shipping, no endless waiting." },
  { icon: Users, title: "Family owned since 2004", text: "Founded by a mechanical engineer who's passionate about gear built to last." },
  { icon: Truck, title: "Australia-wide delivery", text: "Fully insured shipping to every state and territory, backed by real support." },
];

const serviceQuotes = [
  { text: "Steve is one of the most sincere and friendly people you could ever meet. A+++ for service!", name: "Benny Wong" },
  { text: "Went out of their way at every stage. The best supplier of equipment and expert advice.", name: "P A" },
];

export default function StorePresence() {
  const ref = useReveal();
  return (
    <section id="store" className="bg-white py-16">
      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Left: story */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              We&rsquo;re more than a website
            </p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold text-navy md:text-4xl">
              Not just an online business
            </h2>
            <p className="mt-4 max-w-xl text-navy/70">
              Behind Cyberfit is a genuine bricks-and-mortar business &mdash; a real showroom and a
              warehouse packed with stock in Seven Hills, Sydney. Since 2004 our family-run team has
              helped thousands of Australians build home and commercial gyms, with the kind of honest,
              old-fashioned customer service our reviews are full of.
            </p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {points.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky text-navy">
                    <p.icon size={20} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-navy">{p.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-navy/60">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {serviceQuotes.map((q, i) => (
                <div key={i} className="rounded-xl border border-navy/10 bg-gray-50 p-4">
                  <Quote size={16} className="text-brand" />
                  <p className="mt-1 text-sm font-medium leading-snug text-navy/80">&ldquo;{q.text}&rdquo;</p>
                  <p className="mt-1 text-xs font-semibold text-navy/50">&mdash; {q.name}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://maps.app.goo.gl/ZTo8XWBKLxpxp1QW8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 font-heading font-bold text-white transition-colors hover:bg-brand-dark"
              >
                <MapPin size={17} /> Get Directions
              </a>
              <a
                href="tel:+61296206635"
                className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-7 py-3 font-heading font-bold text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <Phone size={17} /> Call the Team
              </a>
            </div>
          </div>

          {/* Right: real store photo collage */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="/images/store-showroom-3.jpg"
                  alt="Cyberfit machines in the Seven Hills warehouse showroom"
                  className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <div className="overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="/images/store-warehouse.jpg"
                  alt="Cyberfit showroom racks and treadmill"
                  className="h-48 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <div className="overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="/images/store-showroom-1.jpg"
                  alt="Cyberfit showroom floor with racks and treadmills"
                  className="h-48 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </div>
            {/* Floating trust badge */}
            <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3 rounded-2xl border border-navy/10 bg-white px-5 py-3 shadow-xl">
              <div className="flex items-center gap-1.5">
                <Star size={18} className="text-brand" fill="currentColor" />
                <span className="font-heading text-lg font-extrabold text-navy">4.8</span>
              </div>
              <span className="h-8 w-px bg-navy/10" />
              <div className="leading-tight">
                <p className="text-xs font-bold text-navy">Seven Hills, NSW</p>
                <p className="text-[11px] text-navy/50">74 Google reviews</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
